import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography,
} from '@execonline-inc/execonline-ui';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CategoryTrackerData } from './Types';
import EntitlementCard from './EntitlementCard';

interface LicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryData: CategoryTrackerData;
}

function LicenseModal({ isOpen, onClose, categoryData }: LicenseModalProps) {
  const { categories, hasUnlicensedPrograms } = categoryData;

  return (
    <Modal open={isOpen} onClose={onClose} data-intercom-target="builder-v2-license-modal">
      <ModalHeader>
        <div className="flex flex-col">
          <Typography variant="subtitleLarge" fontWeight="semibold">
            Collection License Summary
          </Typography>
          <Typography variant="bodySmall" className="text-muted-foreground mt-1">
            Check the programs included in this collection to confirm your organization has access.
          </Typography>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Category cards - horizontal grid */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {categories.map((item) => (
            <EntitlementCard key={item.category} item={item} />
          ))}
        </div>

        {/* Warning banner for unlicensed programs */}
        {hasUnlicensedPrograms && (
          <div className="bg-muted border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon
                className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <Typography variant="bodySmall" fontWeight="semibold" className="text-foreground">
                  Some programs will not be visible to learners
                </Typography>
                <Typography variant="caption" className="text-muted-foreground mt-1">
                  Your organization's license does not include all of the program types in this
                  collection. Contact ExecOnline for access.
                </Typography>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {categories.length === 0 && (
          <div className="text-center py-8">
            <Typography variant="bodySmall" className="text-muted-foreground">
              No programs in this collection yet.
            </Typography>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          onClick={onClose}
          className="w-full"
          data-intercom-target="builder-v2-license-modal-close-button"
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default LicenseModal;
